import { BalancePercentageOptionsEnum } from "components/TradeLeg/sections/BalancePercentages";
import { useGetMaxAmountToWrap } from "hooks/wrap/useGetMaxAmountToWrap";
import { GlobalStore, MakerToken, SelectedToken } from "store/global/GlobalStore";
import { Token } from "store/global/TokenEnum";
import { Balance } from "store/global/getBalances";
import { Amount } from "types/amount";
import { Quote } from "types/quote";
import { Chain } from "viem";
import { isNativeToken } from "./wrapUtils";
import { cloneDeep } from "lodash-es";

export type CounterParty = "maker" | "taker";

export const canLock = (counterParty: CounterParty, counterPartyTokens: Array<SelectedToken>) => {
  if (counterParty === "maker") {
    // We only allow locking if there are more than two unlocked entries remaining
    return (counterPartyTokens as Array<MakerToken>).filter(entry => !entry.isLocked).length > 2;
  }
  return false;
};

export const getAmount = (
  symbol: Token,
  quote: Quote | undefined,
  counterParty: CounterParty,
  selectedToken: SelectedToken | undefined
): Amount => {
  if (counterParty === "taker" && selectedToken?.amount) {
    return selectedToken.amount;
  }
  if (symbol && quote) {
    const makerToken = quote.makerTokens[symbol];
    if (makerToken) {
      return makerToken.amount ?? Amount.ZERO;
    }
  }
  return Amount.ZERO;
};

export const handleBalanceOption = async (
  symbol: Token,
  counterParty: CounterParty,
  balanceOption: BalancePercentageOptionsEnum,
  network: Chain,
  balance: Balance,
  getMaxAmountToWrap: ReturnType<typeof useGetMaxAmountToWrap>,
  decimals: number
) => {
  const percent =
    balanceOption !== BalancePercentageOptionsEnum.Max ? new Amount(balanceOption) : Amount.ZERO;
  if (counterParty === "taker" && symbol) {
    let amountToUpdate: Amount = Amount.ZERO;
    if (isNativeToken(symbol, network)) {
      const maxAmount = (await getMaxAmountToWrap(symbol)) ?? Amount.ZERO;
      amountToUpdate =
        balanceOption === BalancePercentageOptionsEnum.Max
          ? maxAmount
          : new Amount(maxAmount.mul(percent).value.toFixed(decimals));
    } else {
      const currentBalance: Amount = balance?.balance ?? Amount.ZERO;
      amountToUpdate =
        balanceOption === BalancePercentageOptionsEnum.Max
          ? currentBalance
          : new Amount(currentBalance.mul(percent).value.toFixed(decimals));
    }

    GlobalStore.update(s => {
      s.takerTokens = s.takerTokens.map(entry => {
        if (entry.token === symbol) {
          const updatedEntry = cloneDeep(entry);
          updatedEntry.amount = amountToUpdate;
          return updatedEntry;
        }
        return entry;
      });
    });
  }
};
